import React, { useEffect, useState } from 'react';
import { Form, Input } from 'antd';
import {
  MailOutlined,
  UserOutlined,
  LockOutlined,
  SafetyOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { type AppDispatch } from '../../redux/store';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../components/ToastComponent/store/toastMiddleware';
import BrandLogo from '../../assets/images/goldpulse_logo.png';
import AuthButton, { AuthSpinner } from '../../components/AuthButton';
import OtpInput from '../SignUp/OtpInput';
import {
  formatTime,
  getStrength,
  PASSWORD_PATTERN,
} from '../SignUp/authUtils';
import {
  resetPassword,
  sendResetOtp,
  type ForgotPasswordResponse,
} from './forgotPasswordApi';
import AuthHero from '../../components/AuthHero';
import JewelBackdrop from '../../components/AuthHero/JewelBackdrop';
import '../SignIn/index.scss';
import '../SignUp/index.scss';
import './index.scss';

type Step = 'username' | 'otp' | 'reset';

interface ResetValues {
  password: string;
  confirmPassword: string;
}

const OTP_LENGTH = 6;
const GENERIC_MESSAGE = 'Something went wrong. Please try again.';
const iconStyle = { color: '#64748B', marginRight: 8 };

const ForgotPasswordPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [usernameForm] = Form.useForm<{ userName: string }>();
  const [resetForm] = Form.useForm<ResetValues>();

  const [step, setStep] = useState<Step>('username');
  const [userName, setUserName] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [expiresLeft, setExpiresLeft] = useState(0);
  const [resendLeft, setResendLeft] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const password = Form.useWatch('password', resetForm);
  const strength = getStrength(password);

  // Expiry + resend countdowns
  useEffect(() => {
    if (expiresLeft <= 0 && resendLeft <= 0) return;
    const timer = window.setTimeout(() => {
      setExpiresLeft((s) => Math.max(s - 1, 0));
      setResendLeft((s) => Math.max(s - 1, 0));
    }, 1000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [expiresLeft, resendLeft]);

  const isExpired = step !== 'username' && expiresLeft <= 0;
  const canResend = resendLeft <= 0 || isExpired;

  const goToSignIn = () => {
    navigate('/sign-in');
  };

  const backToUsername = () => {
    setStep('username');
    setOtp('');
    setOtpError('');
    resetForm.resetFields();
  };

  const messageOf = (data: any): string =>
    typeof data?.message === 'string' ? data.message : GENERIC_MESSAGE;

  const startOtpTimers = (res: ForgotPasswordResponse) => {
    setMaskedEmail(res.email);
    setExpiresLeft(res.expiresInSeconds);
    setResendLeft(res.resendAfterSeconds);
    setOtp('');
    setOtpError('');
  };

  // Step 1 (and Resend): POST /users/forgot-password
  const requestOtp = async (name: string, isResend: boolean) => {
    const setBusy = isResend ? undefined : setIsSending;
    setBusy?.(true);
    try {
      const res = await sendResetOtp({ userName: name });
      setUserName(name);
      startOtpTimers(res);
      setStep('otp');
      if (isResend) dispatch(showSuccessMessage('A new OTP has been sent'));
    } catch (error: any) {
      const data = error?.response?.data;
      const status = error?.response?.status;
      const message = messageOf(data);
      if (data?.field === 'userName') {
        // Username not found / no email registered: show it under the Username input
        if (isResend) backToUsername();
        usernameForm.setFields([{ name: 'userName', errors: [message] }]);
      } else {
        // 429 (keep the resend timer running) and 500 (let the user retry)
        dispatch(showErrorMessage(status === undefined ? GENERIC_MESSAGE : message));
      }
    } finally {
      setBusy?.(false);
    }
  };

  const onSendOtp = async (values: { userName: string }) => {
    if (isSending) return;
    await requestOtp(values.userName.trim(), false);
  };

  const onResend = async () => {
    if (!canResend || isSending || isResetting) return;
    setIsSending(true);
    try {
      await requestOtp(userName, true);
    } finally {
      setIsSending(false);
    }
  };

  // The server checks the OTP together with the new password, so this step only gates on a full code
  const onVerify = () => {
    if (otp.length < OTP_LENGTH) {
      setOtpError(`Enter the ${OTP_LENGTH}-digit code`);
      return;
    }
    setStep('reset');
  };

  // Step 2: POST /users/reset-password
  const onReset = async (values: ResetValues) => {
    if (isResetting) return;
    if (otp.length < OTP_LENGTH) {
      setOtpError(`Enter the ${OTP_LENGTH}-digit code`);
      return;
    }
    if (values.password !== values.confirmPassword) {
      resetForm.setFields([
        { name: 'confirmPassword', errors: ['Passwords do not match'] },
      ]);
      return;
    }
    setIsResetting(true);
    try {
      await resetPassword({
        userName,
        otp,
        newPassword: values.password,
        confirmPassword: values.confirmPassword,
      });
      dispatch(showSuccessMessage('Password updated. Please sign in.'));
      goToSignIn();
    } catch (error: any) {
      const data = error?.response?.data;
      const status = error?.response?.status;
      const message = messageOf(data);
      const keys: string[] | undefined = data?.validation?.body?.keys;

      if (Array.isArray(keys) && keys.length > 0) {
        const validationMessage: string =
          data?.validation?.body?.message ?? 'Invalid value';
        if (keys.includes('otp')) {
          setOtp('');
          setOtpError(validationMessage);
          setStep('otp');
        }
        const fields = keys
          .filter((key) => key !== 'otp' && key !== 'userName')
          .map((key) => ({
            name: key === 'newPassword' ? 'password' : (key as keyof ResetValues),
            errors: [validationMessage],
          }));
        if (fields.length > 0) resetForm.setFields(fields);
      } else if (status === 429) {
        // Pending OTP is deleted after too many wrong attempts
        dispatch(showErrorMessage(message));
        backToUsername();
      } else if (data?.field === 'userName') {
        backToUsername();
        usernameForm.setFields([{ name: 'userName', errors: [message] }]);
      } else if (data?.field === 'otp') {
        setOtp('');
        setOtpError(message);
        setStep('otp');
        if (/expired/i.test(message)) setExpiresLeft(0);
      } else {
        dispatch(showErrorMessage(message));
      }
    } finally {
      setIsResetting(false);
    }
  };

  const linkProps = (action: () => void) => ({
    role: 'button',
    tabIndex: 0,
    onClick: action,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') action();
    },
  });

  return (
    <div className="sign-in-container sign-up-page forgot-page">
      <div className="bg-stripe" />
      <div className="bg-red">
        <JewelBackdrop />
        <span className="orb orb-1" />
        <span className="orb orb-2" />
      </div>

      <header className="top-bar">
        <div className="brand">
          <img src={BrandLogo} alt="Gold Pulse" />
        </div>
        <div className="request-pill">
          Remembered it?{' '}
          <span className="signup-link" {...linkProps(goToSignIn)}>
            Sign in
          </span>
        </div>
      </header>

      <AuthHero />

      <div className="form-container">
        <div className="form-card">
          <div className="signup-step" hidden={step !== 'username'}>
            <p className="eyebrow">
              <SafetyOutlined /> ACCOUNT RECOVERY
            </p>
            <p className="title">Forgot password?</p>
            <p className="subtitle">
              Enter your username and we&apos;ll send a one-time code to the
              email address registered with it.
            </p>
            <Form
              form={usernameForm}
              name="forgot_username_form"
              layout="vertical"
              requiredMark={false}
              onFinish={onSendOtp}
            >
              <Form.Item
                label="Username"
                name="userName"
                rules={[
                  { required: true, message: 'Please enter your username' },
                  {
                    whitespace: true,
                    message: 'Please enter your username',
                  },
                ]}
              >
                <Input
                  className="signin__input"
                  placeholder="Enter your username"
                  prefix={<UserOutlined style={iconStyle} />}
                />
              </Form.Item>
              <Form.Item>
                <AuthButton
                  htmlType="submit"
                loading={isSending}
 loadingText="Sending OTP...">
                  Send OTP <ArrowRightOutlined />
                </AuthButton>
              </Form.Item>
            </Form>
            <p className="otp-back">
              <span className="signup-link" {...linkProps(goToSignIn)}>
                ← Back to sign in
              </span>
            </p>
          </div>

          {step === 'otp' && (
            <div className="signup-step otp-step">
              <p className="eyebrow">
                <MailOutlined /> VERIFY EMAIL
              </p>
              <p className="title">Check your email</p>
              <p className="subtitle">
                We sent a {OTP_LENGTH}-digit code to{' '}
                <strong>{maskedEmail}</strong>. Enter it below to continue.
              </p>
              <OtpInput
                value={otp}
                onChange={(value) => {
                  setOtp(value);
                  setOtpError('');
                }}
                length={OTP_LENGTH}
                hasError={otpError !== ''}
              />
              <p className="otp-error">{otpError}</p>
              <p className="otp-resend">
                {isExpired ? (
                  <span className="otp-error-text">OTP expired</span>
                ) : (
                  <>
                    Code expires in{' '}
                    <span className="otp-timer">{formatTime(expiresLeft)}</span>
                  </>
                )}
              </p>
              <AuthButton onClick={onVerify}
                disabled={otp.length < OTP_LENGTH || isExpired}
              >
                Verify OTP <ArrowRightOutlined />
              </AuthButton>
              <p className="otp-resend">
                Didn&apos;t receive the code?{' '}
                {canResend ? (
                  <span
                    className="signup-link"
                    {...linkProps(() => {
                      void onResend();
                    })}
                  >
                    {isSending ? (
                      <>
                        <AuthSpinner /> Sending...
                      </>
                    ) : (
                      'Resend code'
                    )}
                  </span>
                ) : (
                  <span className="otp-timer">
                    Resend in {formatTime(resendLeft)}
                  </span>
                )}
              </p>
              <p className="otp-back">
                <span className="signup-link" {...linkProps(backToUsername)}>
                  ← Use a different username
                </span>
              </p>
            </div>
          )}

          <div className="signup-step" hidden={step !== 'reset'}>
            <p className="eyebrow">
              <LockOutlined /> NEW PASSWORD
            </p>
            <p className="title">Set a new password</p>
            <p className="subtitle">
              Code entered. Choose a strong password you haven&apos;t used
              before.
            </p>

            <Form
              form={resetForm}
              name="forgot_reset_form"
              layout="vertical"
              requiredMark={false}
              onFinish={onReset}
            >
              <Form.Item
                label="New password"
                name="password"
                rules={[
                  { required: true, message: 'Please create a password' },
                  {
                    pattern: PASSWORD_PATTERN,
                    message: 'Password does not meet the requirements',
                  },
                ]}
              >
                <Input.Password
                  className="signin__input"
                  placeholder="Create a new password"
                  prefix={<LockOutlined style={iconStyle} />}
                />
              </Form.Item>
              <Form.Item
                label="Confirm new password"
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please re-enter the password' },
                  ({ getFieldValue }) => ({
                    validator: async (_, value: string | undefined) => {
                      if (
                        value === undefined ||
                        value === '' ||
                        getFieldValue('password') === value
                      ) {
                        return;
                      }
                      throw new Error('Passwords do not match');
                    },
                  }),
                ]}
              >
                <Input.Password
                  className="signin__input"
                  placeholder="Re-enter new password"
                  prefix={<LockOutlined style={iconStyle} />}
                />
              </Form.Item>
              <div className="strength-meter">
                {[1, 2, 3, 4].map((level) => (
                  <span
                    key={level}
                    className={`bar${strength >= level ? ` on level-${strength}` : ''}`}
                  />
                ))}
              </div>
              <p className="strength-hint">
                Use 8+ characters with a number and a symbol.
              </p>
              <Form.Item>
                <AuthButton
                  htmlType="submit"
                loading={isResetting}
 loadingText="Resetting password...">
                  Reset password <ArrowRightOutlined />
                </AuthButton>
              </Form.Item>
            </Form>
            <p className="otp-back">
              <span
                className="signup-link"
                {...linkProps(() => {
                  setStep('otp');
                })}
              >
                ← Back
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
